// The multi-select action bar. Rendered above the list on every email surface once at
// least one row is checked; every button maps 1:1 onto a single-row action, so nothing
// is reachable in bulk that isn't reachable one at a time.
//
// Presentational only — it owns no selection state and performs no writes. The surface
// owns the selection (useEmailSelection) and the write (useEmailActions.bulkApply), which
// is what lets the same bar serve /inbox, the account tabs and the pop-out.

import { useState } from "react";
import { Loader2, Tag, UserPlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { categoryColor } from "@/lib/email/format";

export interface BulkEmailActionBarProps {
  count: number;
  /** Assignable roster (contractors/portal clients excluded server-side). */
  users: { id: string; name: string }[];
  currentUserId: string;
  knownCategories: string[];
  busy: boolean;
  /** Pop-out chrome (InboxOutlook's neutral-on-white Outlook look), not a dark theme. */
  dark?: boolean;
  onMarkRead: (isRead: boolean) => void;
  onAssign: (user: { id: string; name: string } | null) => void; // null = unassign
  onFlag: (flagged: boolean) => void;
  onCategorize: (label: string) => void;
  onClear: () => void;
}

export function BulkEmailActionBar({
  count,
  users,
  currentUserId,
  knownCategories,
  busy,
  dark,
  onMarkRead,
  onAssign,
  onFlag,
  onCategorize,
  onClear,
}: BulkEmailActionBarProps) {
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSearch, setAssignSearch] = useState("");
  const [labelOpen, setLabelOpen] = useState(false);
  const [newLabel, setNewLabel] = useState("");

  if (count === 0) return null;

  // "Claim to me" needs a display name for the optimistic patch, so it is only offered when
  // the signed-in user is actually in the assignable roster — inventing a placeholder name
  // would render a wrong assignee chip until the refetch corrects it.
  const me = users.find((u) => u.id === currentUserId) ?? null;
  const filteredUsers = users.filter((u) =>
    u.name.toLowerCase().includes(assignSearch.trim().toLowerCase()),
  );

  const btn = cn("shrink-0", dark ? "h-7 gap-1.5 text-neutral-700" : "h-8 gap-1.5");
  const btnVariant = dark ? ("ghost" as const) : ("outline" as const);

  const applyNewLabel = () => {
    // Guard busy here, not only on the trigger: Radix closes the menu on select today, which
    // incidentally prevents a double-submit, but nothing guarantees that survives a redesign
    // (a keep-open multi-label picker is the obvious one).
    if (busy) return;
    const name = newLabel.trim();
    if (!name) return;
    onCategorize(name);
    setNewLabel("");
    setLabelOpen(false);
  };

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-1.5 rounded-lg border px-2.5 py-1.5 shadow-sm",
        dark ? "border-neutral-200 bg-white text-neutral-900" : "bg-card",
      )}
    >
      <span
        className={cn(
          "flex shrink-0 items-center gap-1.5 pr-1 text-sm font-medium",
          dark && "text-neutral-800",
        )}
      >
        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
        {count} selected
      </span>

      <span className={cn("mx-0.5 h-5 w-px shrink-0", dark ? "bg-neutral-200" : "bg-border")} />

      <Button size="sm" variant={btnVariant} className={btn} disabled={busy} onClick={() => onMarkRead(true)}>
        Mark read
      </Button>
      <Button size="sm" variant={btnVariant} className={btn} disabled={busy} onClick={() => onMarkRead(false)}>
        Mark unread
      </Button>

      {me && (
        <Button size="sm" variant={btnVariant} className={btn} disabled={busy} onClick={() => onAssign(me)}>
          <UserPlus className="h-3.5 w-3.5" />
          Claim to me
        </Button>
      )}

      <DropdownMenu
        open={assignOpen}
        onOpenChange={(open) => {
          setAssignOpen(open);
          if (!open) setAssignSearch("");
        }}
      >
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant={btnVariant} className={btn} disabled={busy}>
            Assign…
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72">
          <DropdownMenuLabel>Assign {count} to</DropdownMenuLabel>
          <div className="px-2 pb-1.5">
            <Input
              autoFocus
              value={assignSearch}
              onChange={(e) => setAssignSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              placeholder="Search staff…"
              className="h-7 text-xs"
            />
          </div>
          <DropdownMenuSeparator />
          <div className="max-h-56 overflow-auto">
            {filteredUsers.length === 0 ? (
              <DropdownMenuItem disabled>No matching staff</DropdownMenuItem>
            ) : (
              filteredUsers.map((u) => (
                <DropdownMenuItem
                  key={u.id}
                  className="max-w-full"
                  onClick={() => onAssign({ id: u.id, name: u.name })}
                >
                  <span className="truncate">{u.name}</span>
                  {u.id === currentUserId && (
                    <span className="ml-1 shrink-0 text-xs text-muted-foreground">(me)</span>
                  )}
                </DropdownMenuItem>
              ))
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <Button size="sm" variant={btnVariant} className={btn} disabled={busy} onClick={() => onAssign(null)}>
        Unassign
      </Button>

      <Button size="sm" variant={btnVariant} className={btn} disabled={busy} onClick={() => onFlag(true)}>
        Flag
      </Button>
      <Button size="sm" variant={btnVariant} className={btn} disabled={busy} onClick={() => onFlag(false)}>
        Unflag
      </Button>

      <DropdownMenu open={labelOpen} onOpenChange={setLabelOpen}>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant={btnVariant} className={btn} disabled={busy}>
            <Tag className="h-3.5 w-3.5" />
            Label…
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-72 w-60 overflow-auto">
          <DropdownMenuLabel>Apply label to {count}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {knownCategories.length === 0 && <DropdownMenuItem disabled>No labels yet</DropdownMenuItem>}
          {knownCategories.map((name) => (
            <DropdownMenuItem key={name} onClick={() => onCategorize(name)}>
              <span
                className={cn(
                  "mr-1.5 inline-block h-2.5 w-2.5 shrink-0 rounded-full",
                  categoryColor(name).split(" ")[0],
                )}
              />
              <span className="truncate">{name}</span>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <div className="flex items-center gap-1 px-2 py-1.5">
            <Input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") applyNewLabel();
              }}
              placeholder="New label…"
              className="h-7 text-xs"
            />
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-xs"
              onClick={applyNewLabel}
              disabled={busy || !newLabel.trim()}
            >
              Apply
            </Button>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <Button
        size="sm"
        variant="ghost"
        className={cn("ml-auto shrink-0 gap-1", dark ? "h-7 text-neutral-600" : "h-8")}
        onClick={onClear}
        title="Clear selection"
      >
        <X className="h-3.5 w-3.5" />
        Clear
      </Button>
    </div>
  );
}
